/**
 * How far the view turns per pointer count, for every mode at once.
 *
 * A device reports look motion in pointer counts and a thumb drag converts pixels to the same unit,
 * so one number serves a mouse and a phone. It used to be a constant in each mode's control map,
 * which meant two copies of the same 0.0022 and no way for a player to change either.
 *
 * The quantity is degrees, not radians and not a multiplier. Degrees is what a slider can show and
 * what one player can read out to another; radians per count is four leading zeros of nothing. The
 * conversion happens once, here, so a control map still multiplies by radians and nothing
 * downstream learns that a setting exists.
 *
 * Read per tick rather than captured. That is what lets the settings screen change the value with
 * a game running behind it and have the next frame turn at the new rate.
 */

import { clamp, degToRad } from '@/sim/math';

/**
 * Roughly a 40 cm turn for 360 degrees at 800 dpi, which is where both control maps sat as a
 * constant before this file existed.
 */
export const DEFAULT_LOOK_DEGREES_PER_COUNT = 0.126;

/** Slow enough for a bolt gun at distance. Below this a full turn is most of a desk. */
export const MIN_LOOK_DEGREES_PER_COUNT = 0.02;

/** Fast enough to spin on the spot. Above this a flick leaves the target behind. */
export const MAX_LOOK_DEGREES_PER_COUNT = 0.6;

let degreesPerCount = DEFAULT_LOOK_DEGREES_PER_COUNT;

export function lookDegreesPerCount(): number {
  return degreesPerCount;
}

/** Clamped here as well as in the settings layer: this is the value the game actually turns on. */
export function setLookDegreesPerCount(degrees: number): void {
  degreesPerCount = clamp(degrees, MIN_LOOK_DEGREES_PER_COUNT, MAX_LOOK_DEGREES_PER_COUNT);
}

/** What a control map multiplies a pointer count by. */
export function lookRadiansPerCount(): number {
  return degToRad(degreesPerCount);
}
